import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  getRedirectResult,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signOut,
  type User,
} from "firebase/auth";
import type { SupportCustomClaims } from "@srd/shared";
import { auth, googleProvider, startTotpMfaEnrollmentPlaceholder } from "../firebase";

interface AuthContextValue {
  user: User | null;
  claims: SupportCustomClaims | null;
  loading: boolean;
  authError: string | null;
  clearAuthError: () => void;
  signInGoogle: () => Promise<void>;
  signInEmail: (email: string, password: string) => Promise<void>;
  signOutUser: () => Promise<void>;
  startMfaPlaceholder: () => Promise<string>;
  refreshClaims: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function readSupportClaims(user: User): Promise<SupportCustomClaims | null> {
  const token = await user.getIdTokenResult(true);
  const role = token.claims.role;
  const tenantId = token.claims.tenantId;
  if (
    (role !== "technician" && role !== "admin" && role !== "auditor") ||
    typeof tenantId !== "string" ||
    !tenantId
  ) {
    return null;
  }
  return {
    role,
    tenantId,
    canUnattended: Boolean(token.claims.canUnattended),
    deviceGroups: Array.isArray(token.claims.deviceGroups)
      ? (token.claims.deviceGroups as unknown[]).filter((g): g is string => typeof g === "string")
      : undefined,
    mfaRequired:
      token.claims.mfaRequired === undefined ? true : Boolean(token.claims.mfaRequired),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<SupportCustomClaims | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Redirect SSO must be resolved before we treat auth as settled.
    void (async () => {
      try {
        await getRedirectResult(auth);
      } catch (err) {
        if (!cancelled) {
          setAuthError(err instanceof Error ? err.message : String(err));
        }
      }
    })();

    const unsub = onAuthStateChanged(auth, (next) => {
      void (async () => {
        if (cancelled) return;
        setUser(next);
        if (!next) {
          setClaims(null);
          setLoading(false);
          return;
        }
        try {
          setClaims(await readSupportClaims(next));
        } catch {
          setClaims(null);
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      claims,
      loading,
      authError,
      clearAuthError: () => setAuthError(null),
      signInGoogle: async () => {
        // Redirect avoids auth/popup-closed-by-user on custom domains / COOP / SSO.
        setAuthError(null);
        await signInWithRedirect(auth, googleProvider);
      },
      signInEmail: async (email, password) => {
        setAuthError(null);
        await signInWithEmailAndPassword(auth, email, password);
      },
      signOutUser: async () => {
        await signOut(auth);
      },
      startMfaPlaceholder: startTotpMfaEnrollmentPlaceholder,
      refreshClaims: async () => {
        if (!auth.currentUser) return;
        setClaims(await readSupportClaims(auth.currentUser));
      },
    }),
    [user, claims, loading, authError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
